// انتزاع احراز هویت پوسته: پیاده‌سازی OIDC واقعی (oidc-client-ts) و پیاده‌سازی ساختگی برای تست.
import type { User, UserManager } from 'oidc-client-ts';
import { createUserManager, isSigninCallback, POPUP_CALLBACK_PATH, type OidcConfig } from './userManager';

/** هویت Keycloak کاربر (بدون توکن؛ توکن فقط با getAccessToken). */
export interface AuthUser {
  /** شناسه Keycloak (sub). */
  subject: string;
  /** نام کاربری (موبایل — BR-IAM-001). */
  username: string | undefined;
  name: string | undefined;
  email: string | undefined;
  /** Realm Roleهای پلتفرم (Claim platform_roles). */
  platformRoles: readonly string[];
}

export type InitResult =
  | { kind: 'authenticated'; user: AuthUser; returnTo?: string }
  | { kind: 'anonymous' }
  | { kind: 'popup-callback' };

export interface AuthClient {
  /** پردازش Callback ورود یا پنجره Popup و بازیابی نشست در حافظه. */
  init(): Promise<InitResult>;
  /** انتقال به صفحه ورود Keycloak. */
  login(returnTo?: string): Promise<void>;
  logout(): Promise<void>;
  /** تغییر رمز با Application Initiated Action کیکلاک (kc_action=UPDATE_PASSWORD) و بازگشت به returnTo. */
  changePassword(returnTo?: string): Promise<void>;
  /** پیکربندی TOTP با Application Initiated Action کیکلاک (kc_action=CONFIGURE_TOTP؛ US-IAM-003 — AP-IAM-03). */
  configureTotp(returnTo?: string): Promise<void>;
  /** توکن دسترسی Keycloak (forceRefresh ← تمدید با Refresh Token). */
  getAccessToken(options?: { forceRefresh?: boolean }): Promise<string | undefined>;
  /** ورود مجدد سریع پس از قفل صفحه (Popup با prompt=login). null ← انصراف یا شکست. */
  reauthenticate(loginHint?: string): Promise<AuthUser | null>;
  /** توقف و ادامه تمدید خودکار (در حالت قفل). */
  pauseRenew(): void;
  resumeRenew(): void;
  /** خروج نشست از بیرون (انقضای Refresh، خروج سراسری). */
  onSessionEnded(listener: () => void): () => void;
}

function toAuthUser(user: User): AuthUser {
  const profile = user.profile as Record<string, unknown>;
  const roles = profile['platform_roles'];
  return {
    subject: user.profile.sub,
    username:
      typeof profile['preferred_username'] === 'string'
        ? (profile['preferred_username'] as string)
        : undefined,
    name: typeof profile['name'] === 'string' ? (profile['name'] as string) : undefined,
    email: typeof profile['email'] === 'string' ? (profile['email'] as string) : undefined,
    platformRoles: Array.isArray(roles) ? roles.filter((r): r is string => typeof r === 'string') : [],
  };
}

interface RedirectState {
  returnTo?: string;
}

/** پیاده‌سازی واقعی با oidc-client-ts. */
export function createOidcAuthClient(
  config: OidcConfig,
  manager: UserManager = createUserManager(config),
): AuthClient {
  let refreshing: Promise<User | null> | null = null;

  return {
    async init() {
      if (window.location.pathname === POPUP_CALLBACK_PATH) {
        await manager.signinPopupCallback();
        return { kind: 'popup-callback' };
      }
      if (isSigninCallback()) {
        const user = await manager.signinRedirectCallback();
        const state = (user.state ?? {}) as RedirectState;
        const returnTo = state.returnTo && state.returnTo.startsWith('/') ? state.returnTo : '/';
        window.history.replaceState(window.history.state, '', returnTo);
        return { kind: 'authenticated', user: toAuthUser(user), returnTo };
      }
      const existing = await manager.getUser();
      if (existing && !existing.expired) {
        return { kind: 'authenticated', user: toAuthUser(existing) };
      }
      return { kind: 'anonymous' };
    },
    async login(returnTo) {
      const target = returnTo ?? `${window.location.pathname}${window.location.search}`;
      await manager.signinRedirect({ state: { returnTo: target } satisfies RedirectState });
    },
    async changePassword(returnTo) {
      const target = returnTo ?? `${window.location.pathname}${window.location.search}`;
      await manager.signinRedirect({
        state: { returnTo: target } satisfies RedirectState,
        extraQueryParams: { kc_action: 'UPDATE_PASSWORD' },
      });
    },
    async configureTotp(returnTo) {
      const target = returnTo ?? `${window.location.pathname}${window.location.search}`;
      await manager.signinRedirect({
        state: { returnTo: target } satisfies RedirectState,
        extraQueryParams: { kc_action: 'CONFIGURE_TOTP' },
      });
    },
    async logout() {
      try {
        await manager.signoutRedirect();
      } finally {
        await manager.removeUser();
      }
    },
    async getAccessToken(options) {
      let user = await manager.getUser();
      if (options?.forceRefresh || !user || user.expired) {
        refreshing ??= manager.signinSilent().finally(() => {
          refreshing = null;
        });
        try {
          user = await refreshing;
        } catch {
          return undefined;
        }
      }
      return user?.access_token;
    },
    async reauthenticate(loginHint) {
      try {
        const user = await manager.signinPopup({ prompt: 'login', login_hint: loginHint, max_age: 0 });
        return toAuthUser(user);
      } catch {
        return null;
      }
    },
    pauseRenew() {
      manager.stopSilentRenew();
    },
    resumeRenew() {
      manager.startSilentRenew();
    },
    onSessionEnded(listener) {
      const handler = () => listener();
      // AP-OFF-01 (ARD-offline §۴، C-AGENT-1548): شکست تمدید به‌خاطر قطع شبکه نشست را پایان نمی‌دهد (ادامه نشست موجود تا ۸ ساعت)؛ فقط پاسخ
      // خطای OAuth کیکلاک (مثل invalid_grant — Refresh منقضی یا خروج سراسری) نشست را می‌بندد.
      const renewError = (error: Error) => {
        if (isNetworkFailure(error)) {
          return;
        }
        listener();
      };
      manager.events.addSilentRenewError(renewError);
      manager.events.addUserSignedOut(handler);
      return () => {
        manager.events.removeSilentRenewError(renewError);
        manager.events.removeUserSignedOut(handler);
      };
    },
  };
}

/**
 * خطای تمدید از قطع شبکه است (نه پاسخ OAuth کیکلاک): مرورگر آفلاین، یا خطایی بدون فیلد «error» پروتکل OAuth (TypeError شبکه، Timeout).
 */
export function isNetworkFailure(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }
  const oauth = typeof error === 'object' && error !== null && typeof (error as { error?: unknown }).error === 'string';
  return !oauth;
}

export interface FakeAuthOptions {
  user?: AuthUser | null;
  token?: string;
  /** کاربر پس از reauthenticate (پیش‌فرض همان کاربر). */
  reauthUser?: AuthUser | null;
}

export const FAKE_USER: AuthUser = {
  subject: '0192f1a0-0000-7000-8000-00000000a001',
  username: '09120000001',
  name: 'Demo Owner',
  email: 'owner@demo.local',
  platformRoles: [],
};

/** پیاده‌سازی ساختگی برای Vitest. */
export function createFakeAuthClient(options: FakeAuthOptions = {}) {
  const listeners = new Set<() => void>();
  const user = options.user === undefined ? FAKE_USER : options.user;
  const calls = {
    login: 0,
    logout: 0,
    changePassword: 0,
    configureTotp: 0,
    reauthenticate: 0,
    pauseRenew: 0,
    resumeRenew: 0,
  };
  const client: AuthClient = {
    async init() {
      return user ? { kind: 'authenticated', user } : { kind: 'anonymous' };
    },
    async login() {
      calls.login += 1;
    },
    async logout() {
      calls.logout += 1;
    },
    async changePassword() {
      calls.changePassword += 1;
    },
    async configureTotp() {
      calls.configureTotp += 1;
    },
    async getAccessToken() {
      return options.token ?? 'kc-token';
    },
    async reauthenticate() {
      calls.reauthenticate += 1;
      return options.reauthUser === undefined ? user : options.reauthUser;
    },
    pauseRenew() {
      calls.pauseRenew += 1;
    },
    resumeRenew() {
      calls.resumeRenew += 1;
    },
    onSessionEnded(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
  return { client, calls, endSession: () => listeners.forEach((l) => l()) };
}
